import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { CircleStop, Play } from 'lucide-react'
import { patientService } from '../../../services/patientApi'

function shiftClock(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

function DesktopShiftButton({ patientId, patientName, lifted = false, onStatus }) {
  const [shift, setShift] = useState({ required: false, open: null })
  const [busy, setBusy] = useState(false)
  const [sheet, setSheet] = useState(null)
  const [sheetError, setSheetError] = useState('')

  const loadShift = useCallback(async () => {
    try {
      const response = await patientService.shiftStatus()
      const next = response.data || { required: false, open: null }
      setShift(next)
      onStatus?.(next)
    } catch {
      const next = { required: false, open: null }
      setShift(next)
      onStatus?.(next)
    }
  }, [onStatus])

  useEffect(() => {
    loadShift()
    const refresh = () => loadShift()
    window.addEventListener('wfmed-shift-updated', refresh)
    return () => window.removeEventListener('wfmed-shift-updated', refresh)
  }, [loadShift])

  if (!shift.required) return null

  const linkedHere = Boolean(shift.open && String(shift.open.patient_id) === String(patientId))
  const otherName = shift.open?.patient_name || 'outro paciente'

  const startShift = async () => {
    setBusy(true)
    setSheetError('')
    try {
      const response = await patientService.startShift(patientId)
      const next = response.data || { required: true, open: null }
      setShift(next)
      onStatus?.(next)
      setSheet(null)
      window.dispatchEvent(new Event('wfmed-shift-updated'))
    } catch (err) {
      const message = err.response?.data?.message || 'Não foi possível iniciar o plantão'
      if (err.response?.status === 409) {
        await loadShift()
        setSheet('other')
      } else {
        window.alert(message)
      }
    } finally {
      setBusy(false)
    }
  }

  const endShift = async () => {
    setBusy(true)
    setSheetError('')
    try {
      const response = await patientService.endShift()
      const next = response.data || { required: true, open: null }
      setShift(next)
      onStatus?.(next)
      setSheet(null)
      window.dispatchEvent(new Event('wfmed-shift-updated'))
    } catch (err) {
      setSheetError(err.response?.data?.message || 'Não foi possível encerrar o plantão')
    } finally {
      setBusy(false)
    }
  }

  const onClick = () => {
    if (busy) return
    setSheetError('')
    if (linkedHere) setSheet('end')
    else if (shift.open) setSheet('other')
    else startShift()
  }

  const control = (
    <div className={`patients-shift patients-shift-inline${lifted ? ' is-lifted' : ''}`} data-onboarding="chart-shift">
      <button
        type="button"
        className={`finance-dashboard-period-btn finance-dashboard-period-btn--interactive patients-shift-record${linkedHere ? ' is-open' : ''}`}
        onClick={onClick}
        disabled={busy}
        aria-label={linkedHere ? 'Encerrar plantão' : 'Iniciar plantão'}
        title={linkedHere ? 'Encerrar plantão' : 'Iniciar plantão'}
      >
        {linkedHere ? <CircleStop size={14} /> : <Play size={14} />}
        {linkedHere ? 'Encerrar' : 'Plantão'}
      </button>
      {linkedHere && (
        <span className="patients-shift-since">desde {shiftClock(shift.open.started_at)}</span>
      )}
    </div>
  )

  const confirmation = sheet && createPortal(
    <div className="patients-shift-sheet-layer">
      <button
        type="button"
        className="patients-shift-sheet-overlay"
        aria-label="Fechar"
        onClick={() => { if (!busy) setSheet(null) }}
      />
      <div
        className="patients-shift-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={sheet === 'end' ? 'Encerrar plantão' : 'Plantão em outro paciente'}
      >
        <div className="patients-shift-sheet-handle" />
        <h2>{sheet === 'end' ? 'Encerrar plantão' : 'Plantão em outro paciente'}</h2>
        <p>
          {sheet === 'end'
            ? `Encerrar o plantão com ${patientName || otherName}? O vínculo com este paciente será encerrado e a saída entra no espelho de ponto.`
            : `Seu plantão está vinculado a ${otherName}. Encerre esse plantão antes de iniciar outro.`}
        </p>
        {sheetError && <p className="patients-shift-sheet-error">{sheetError}</p>}
        <div className="patients-shift-sheet-actions">
          <button type="button" className="patients-shift-sheet-cancel" onClick={() => setSheet(null)} disabled={busy}>
            Cancelar
          </button>
          <button type="button" className="patients-shift-sheet-confirm" onClick={endShift} disabled={busy}>
            Encerrar
          </button>
        </div>
      </div>
    </div>,
    document.body
  )

  return (
    <>
      {lifted ? createPortal(control, document.body) : control}
      {confirmation}
    </>
  )
}

export default DesktopShiftButton
